/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */

import { FC, HTMLAttributes, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as CloseIconSvg } from "../../images/CrossIcon.svg";
import cn from "classnames";
import styles from "./Modal.module.sass";
import { createPortal } from "react-dom";

interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
	factor?: "window" | "sidebar";
	handleClose: () => void;
	transparent?: boolean;
	outSide?: boolean;
	title?: string | ReactNode;
	subtitle?: any;
	size?: "sm" | "md" | "sl" | "lg";
	blur?: boolean;
}

/**
 * @description Компонент модалки, создается через React Portal
 *
 * @param {ModalProps} props
 * @param {Function} props.handleClose  - функция закрытия модалки
 * @param {boolean} [props.transparent] - true - блюрит задний фон, false - стирает
 * @param {string} props.className - класс для модалки
 * @param {any} props.children - содержимое модалки
 * @param {boolean} [props.outSide] - true - включает клик по фону, false - отключает
 * @param {string} [props.title] - заголовок модалки, принимает строку
 * @param {any} [props.subtitle] - подзаголовок таблицы, принимает что угодно
 * @param {('sm'|'sl'|'md'|'lg')} [props.size] - подзаголовок таблицы, принимает что угодно
 * @constructor
 */
const ModalPortal: FC<ModalProps> = ({
	factor = "window",
	handleClose,
	transparent = true,
	className,
	children,
	outSide = true,
	size = "sm",
	title = "",
	subtitle = "",
	blur = true,
}) => {
	const [closed, setClosed] = useState(false);

	const headWrapper = useRef<HTMLDivElement | null>(null);

	// const cancelWindow = () => {
	// 	setClosed(true);
	// 	setTimeout(() => {
	// 		handleClose();
	// 	}, 200);
	// };

	useEffect(() => {
		return () => setClosed(false);
	}, []);

	const maxHeight = useMemo(() => {
		return `calc(100vh - ${headWrapper.current?.getBoundingClientRect().height}px)`;
		//eslint-disable-next-line
	}, [headWrapper.current]);

	return createPortal(
		<div
			className={cn(styles.wrapper, styles[`wrapper_${blur ? "blur" : "not-blur"}`], {
				[styles[`cancel_${blur ? "blur" : "not-blur"}`]]: closed,
			})}
			onClick={factor === "sidebar" ? handleClose : outSide && transparent ? handleClose : () => {}}>
			<div
				className={cn(
					factor === "window" ? styles.modal : styles.sidebar,
					styles[`${factor === "window" ? "size" : "sizeSidebar"}_${size}`],
					className,
				)}
				onClick={(e) => e.stopPropagation()}>
				<CloseIconSvg className={styles.closeIcon} onClick={handleClose} />
				<div ref={headWrapper} className={styles.header}>
					<p>{title}</p>
					{subtitle && <div className={styles.subtitle}>{subtitle}</div>}
				</div>
				<div style={{ maxHeight }} className={styles.content}>
					{children}
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default ModalPortal;
